import React, { useState, useEffect, useCallback } from 'react';
import { wireAPI } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

export default function AdminWireIngestion() {
  const [feeds, setFeeds] = useState([]);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [tab, setTab] = useState('feeds'); // feeds | items
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFeed, setNewFeed] = useState({ name: '', url: '', source: 'rss', auto_import: false });
  const { toast } = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [feedsRes, statsRes] = await Promise.all([wireAPI.list(), wireAPI.stats()]);
      setFeeds(feedsRes.data?.feeds || feedsRes.data || []);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const res = await wireAPI.listItems({ limit: 50, status: 'new' });
      setItems(res.data?.items || res.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadData(); loadItems(); }, [loadData, loadItems]);

  const handlePollAll = async () => {
    setPolling(true);
    try {
      await wireAPI.pollAll();
      toast({ title: 'Poll started', description: 'All active feeds are being polled' });
      setTimeout(() => { loadData(); loadItems(); }, 3000);
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
    setPolling(false);
  };

  const handlePollSingle = async (id) => {
    try {
      await wireAPI.poll(id);
      toast({ title: 'Feed polled' });
      setTimeout(() => { loadData(); loadItems(); }, 2000);
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
  };

  const handleImport = async (itemId) => {
    try {
      await wireAPI.importItem(itemId);
      toast({ title: 'Imported as draft article' });
      loadItems();
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
  };

  const handleDiscard = async (itemId) => {
    try {
      await wireAPI.discardItem(itemId);
      setItems(prev => prev.filter(i => (i.id || i._id) !== itemId));
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
  };

  const handleAddFeed = async (e) => {
    e.preventDefault();
    try {
      await wireAPI.create(newFeed);
      toast({ title: 'Feed added' });
      setShowAddForm(false);
      setNewFeed({ name: '', url: '', source: 'rss', auto_import: false });
      loadData();
    } catch (err) { toast({ title: 'Error', description: err.message, variant: 'destructive' }); }
  };

  const handleDeleteFeed = async (id) => {
    if (!window.confirm('Delete this wire feed?')) return;
    try {
      await wireAPI.delete(id);
      loadData();
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
  };

  if (loading) return <div style={{ padding: 32, color: '#999' }}>Loading wire feeds…</div>;

  return (
    <div style={{ padding: '24px 32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#e8e8e8', margin: 0 }}>Wire Feed Ingestion</h1>
          <p style={{ color: '#888', fontSize: 14, marginTop: 4 }}>
            Manage AP, Reuters, AFP and custom wire feeds
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handlePollAll} disabled={polling} style={btnStyle('#b89b5e')}>
            {polling ? '⟳ Polling…' : '⟳ Poll All Feeds'}
          </button>
          <button onClick={() => setShowAddForm(!showAddForm)} style={btnStyle('#1e5a8a')}>
            + Add Feed
          </button>
        </div>
      </div>

      {/* Stats bar */}
      {stats && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Active Feeds', value: stats.active_feeds ?? feeds.length },
            { label: 'Total Items', value: stats.total_items ?? '—' },
            { label: 'Imported', value: stats.total_imported ?? '—' },
            { label: 'Pending', value: stats.pending ?? items.length },
          ].map(s => (
            <div key={s.label} style={{ background: '#1a1b1e', borderRadius: 8, padding: '12px 20px', flex: 1, border: '1px solid #2a2b2e' }}>
              <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>{s.label}</div>
              <div style={{ color: '#e8e8e8', fontSize: 22, fontWeight: 700, marginTop: 4 }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Add feed form */}
      {showAddForm && (
        <form onSubmit={handleAddFeed} style={{ background: '#1a1b1e', borderRadius: 8, padding: 20, marginBottom: 24, border: '1px solid #2a2b2e' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr auto', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input value={newFeed.name} onChange={e => setNewFeed({ ...newFeed, name: e.target.value })} required style={inputStyle} placeholder="Reuters World" />
            </div>
            <div>
              <label style={labelStyle}>Feed URL</label>
              <input value={newFeed.url} onChange={e => setNewFeed({ ...newFeed, url: e.target.value })} required style={inputStyle} placeholder="https://feeds.reuters.com/reuters/topNews" />
            </div>
            <div>
              <label style={labelStyle}>Source Type</label>
              <select value={newFeed.source} onChange={e => setNewFeed({ ...newFeed, source: e.target.value })} style={inputStyle}>
                {['rss', 'atom', 'api', 'reuters', 'ap', 'afp', 'pa'].map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
              </select>
            </div>
            <button type="submit" style={btnStyle('#1f7a5a')}>Add</button>
          </div>
          <label style={{ display: 'flex', gap: 8, marginTop: 12, color: '#aaa', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={newFeed.auto_import} onChange={e => setNewFeed({ ...newFeed, auto_import: e.target.checked })} />
            Auto-import as draft articles
          </label>
        </form>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid #2a2b2e' }}>
        {['feeds', 'items'].map(t => (
          <button key={t} onClick={() => { setTab(t); if (t === 'items') loadItems(); }}
            style={{ padding: '8px 20px', background: 'none', border: 'none', color: tab === t ? '#b89b5e' : '#888', borderBottom: tab === t ? '2px solid #b89b5e' : '2px solid transparent', cursor: 'pointer', fontWeight: 600, textTransform: 'capitalize' }}>
            {t === 'feeds' ? `Feeds (${feeds.length})` : `Wire Items (${items.length})`}
          </button>
        ))}
      </div>

      {tab === 'feeds' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {feeds.map(f => (
            <div key={f.id || f._id} style={{ background: '#1a1b1e', borderRadius: 8, padding: '14px 20px', border: '1px solid #2a2b2e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#e8e8e8', fontWeight: 600 }}>{f.name || f.url}</div>
                <div style={{ color: '#666', fontSize: 12, marginTop: 2 }}>{f.url} · {f.source?.toUpperCase() || 'RSS'} {f.auto_import ? '· Auto-import' : ''}</div>
                {f.last_checked && <div style={{ color: '#555', fontSize: 11, marginTop: 2 }}>Last polled: {new Date(f.last_checked).toLocaleString()}</div>}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => handlePollSingle(f.id || f._id)} style={btnSmall('#b89b5e')}>Poll</button>
                <button onClick={() => handleDeleteFeed(f.id || f._id)} style={btnSmall('#b3261e')}>Delete</button>
              </div>
            </div>
          ))}
          {feeds.length === 0 && <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No wire feeds configured yet. Add one above.</div>}
        </div>
      )}

      {tab === 'items' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map(item => (
            <div key={item.id || item._id || item.content_hash} style={{ background: '#1a1b1e', borderRadius: 8, padding: '14px 20px', border: '1px solid #2a2b2e' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#e8e8e8', fontWeight: 600, fontSize: 15 }}>{item.title}</div>
                  <div style={{ color: '#888', fontSize: 13, marginTop: 4, lineHeight: 1.4 }}>{item.summary?.slice(0, 200)}{item.summary?.length > 200 ? '…' : ''}</div>
                  <div style={{ color: '#555', fontSize: 11, marginTop: 6 }}>
                    {item.source?.toUpperCase()} · {item.published_at ? new Date(item.published_at).toLocaleString() : 'No date'} · {item.author || 'Wire'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, marginLeft: 16 }}>
                  <button onClick={() => handleImport(item.id || item._id)} style={btnSmall('#1f7a5a')}>Import</button>
                  <button onClick={() => handleDiscard(item.id || item._id)} style={btnSmall('#555')}>Discard</button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No pending wire items. Poll feeds to fetch new content.</div>}
        </div>
      )}
    </div>
  );
}

const btnStyle = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 });
const btnSmall = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 500 });
const labelStyle = { display: 'block', color: '#aaa', fontSize: 12, marginBottom: 4, fontWeight: 500 };
const inputStyle = { width: '100%', background: '#121316', color: '#e8e8e8', border: '1px solid #333', borderRadius: 6, padding: '8px 12px', fontSize: 13 };
